<div class="summary-report flex-col justify-start hidden">
  <!-- Page 1 -->
  <section class="page font-sans text-gray-900 leading-relaxed py-5 px-13 max-w-3xl mx-auto flex flex-col justify-start print:h-screen print:page-break-after-always">

    <header class="mb-10 mt-29 text-[#17365d] flex flex-col items-center text-center">
      <img src="assets/logo.png" alt="Organisation Logo" class="w-lg mb-14 block" style="display:block;" />

      <h1 class="text-4xl font-[510] text-center w-full">ISO/IEC {{ standard === '42001' ? '42001:2023' : '27001:2022' }}</h1>
      <h2 class="text-4xl font-[510] text-center w-full">Readiness Self-Assessment Report</h2>
    </header>

    <section class="mb-8 flex-1 mt-15">
      <table class="w-full border border-black border-collapse table-fixed">
        <thead>
          <tr>
            <th class="py-1.5 px-2 border border-black w-[45%] text-left text-base font-medium">Report Overview</th>
            <th class="py-1.5 px-2 border border-black w-[55%] text-left text-base font-medium">Details</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td class="py-1.5 px-2 border border-black text-sm w-[45%]">Organisation Name</td>
            <td class="py-1.5 px-2 border border-black text-sm w-[55%]">{{ companyName }}</td>
          </tr>

          <tr>
            <td class="py-1.5 px-2 border border-black text-sm w-[45%]">Contact Name</td>
            <td class="py-1.5 px-2 border border-black text-sm w-[55%]">{{ contactName }}</td>
          </tr>

          <tr>
            <td class="py-1.5 px-2 border border-black text-sm w-[45%]">Contact Email</td>
            <td class="py-1.5 px-2 border border-black text-sm w-[55%]">{{ email }}</td>
          </tr>

          <tr>
            <td class="py-1.5 px-2 border border-black text-sm w-[45%]">Assessment Date</td>
            <td class="py-1.5 px-2 border border-black text-sm w-[55%]">{{ assessmentDate }}</td>
          </tr>

          <tr>
            <td class="py-1.5 px-2 border border-black text-sm w-[45%]">Report Version</td>
            <td class="py-1.5 px-2 border border-black text-sm w-[55%]">v1.0.0</td>
          </tr>
        </tbody>
      </table>

      <p class="mt-4 py-1.5 px-2 text-lg">
        This report presents the results of a self-assessment conducted against ISO/IEC {{ standard === '42001' ? '42001:2023' : '27001:2022' }}.
      </p>
    </section>
  </section>

  <!-- Page 2 -->
  <section class="page font-sans text-gray-900 leading-relaxed py-5 px-15 max-w-3xl mx-auto flex flex-col justify-start print:h-screen print:page-break-after-always">

    <header class="mb-3 text-sm">
      ISO/IEC {{ standard === '42001' ? '42001:2023' : '27001:2022' }} Readiness Self-Assessment Report
    </header>

    <section class="mb-3">
      <h3 class="text-md font-bold text-[#366091] pb-1">1. Assessment Approach and Limitations</h3>
      <p class="text-sm">
        This self-assessment is designed to evaluate your organisation's current alignment with the requirements of the standard based on self-reported responses. It serves as a preliminary gap analysis and directional guide. Please note that this is not a substitute for a formal, independent audit, and the accuracy of the insights depends entirely on the completeness and accuracy of the information provided.
      </p>
    </section>

    <section class="mb-3">
      <h3 class="text-md font-bold text-[#366091] pb-1">2. Scoring Methodology</h3>
      <p class="text-sm">
        Responses were assessed using a standardized scale to measure compliance maturity. Each requirement was evaluated as 'Yes' (fully implemented), 'Partial' (partially implemented), 'No' (not implemented), or 'N/A' (not applicable). These responses were weighted to generate an overall readiness score and maturity rating, highlighting key areas requiring remediation.
      </p>
    </section>

    <section class="mb-4">
      <h3 class="text-md font-bold text-[#366091] pb-1">3. Assessment Results and Insights</h3>

      <p class="text-xs"><strong>Overall Readiness: {{ rating?.name }}</strong></p>

      <p class="mt-2 mb-2 text-xs">
        {{ rating?.description }}
      </p>

      <table class="w-full border border-black border-collapse mt-2">
        <thead>
          <tr class="bg-gray-100 text-left">
            <th class="px-1 border border-black text-[10px] w-45">Rating</th>
            <th class="px-1 border border-black text-[10px] w-30">Score Range</th>
            <th class="px-1 border border-black text-[10px]">Description</th>
          </tr>
        </thead>

        <tbody>
          @for (rating of ratings; track rating.name) {
            <tr>
              <td class="p-1 border border-black text-[10px] w-45 whitespace-nowrap">
                {{ rating.name }}
              </td>
              <td class="p-1 border border-black text-[10px] w-30">
                {{ rating.minScore }} – {{ rating.maxScore }}
              </td>
              <td class="p-1 border border-black text-[10px]">
                {{ rating.description }}
              </td>
            </tr>
          }
        </tbody>
      </table>
    </section>

    <footer class="text-gray-600 text-sm mt-2">
      📧 info@auditfixers.com &nbsp;&nbsp; 🌐 www.auditfixers.com
    </footer>
  </section>

  <!-- Page 3 -->
  <section class="page font-sans text-gray-900 leading-relaxed py-5 px-15 max-w-3xl mx-auto flex flex-col justify-start print:h-screen print:page-break-after-always">

    <header class="mb-3 text-sm">
      ISO/IEC {{ standard === '42001' ? '42001:2023' : '27001:2022' }} Readiness Self-Assessment Report
    </header>

    <section class="mb-8 flex-1">
      <h3 class="text-md font-bold text-[#366091] pb-1">4. Detailed Assessment Results and Guidance</h3>

      <p class="text-sm">
        This section summarises your responses and compares them against the expected requirements of the standard. It provides a detailed breakdown of each clause, highlighting the specific controls that have been implemented and identifying areas where further action is required to achieve full compliance.
      </p>

      <table class="w-full border border-black border-collapse mt-2 text-left no-repeat-header">
        <thead>
          <tr class="bg-gray-200">
            <th class="border px-1 py-0.5 text-[10px]">Question #</th>
            <th class="border px-1 py-0.5 text-[10px]">Requirement</th>
            <th class="border px-1 py-0.5 text-[10px]">Clause Ref</th>
            <th class="border px-1 py-0.5 text-[10px]">Your Response</th>
            <th class="border px-1 py-0.5 text-[10px]">What is Expected</th>
          </tr>
        </thead>

        <tbody>
          @for (section of pagedSections['page1']; track section.title) {
            <tr class="bg-gray-100 font-bold text-[10px]">
              <td colspan="5" class="border p-1 py-0.5 text-[10px]">
                Clause {{ section.questions.length > 0 ? getClauseNumber(section.questions[0]?.clauseRef) : 'N/A' }} – {{ section.title }}
              </td>
            </tr>

            @for (question of section.questions; track question.id) {
              <tr class="text-[10px]">
                <td class="border px-1 py-0.5 text-[10px]">{{ question.id }}</td>
                <td class="border px-1 py-0.5 text-[10px]">{{ question.label }}</td>
                <td class="border px-1 py-0.5 text-[10px]">{{ question.clauseRef }}</td>
                <td class="border px-1 py-0.5 text-[10px]"
                    [ngClass]="{
                      'bg-green-100 text-green-800 font-medium': question.value === 'Yes',
                      'bg-yellow-100 text-yellow-800 font-medium': question.value === 'Partial',
                      'bg-red-100 text-red-800 font-medium': question.value === 'No',
                      'bg-gray-100 text-gray-800 font-medium': question.value === 'N/A'
                    }">{{ question.value || '-' }}</td>
                <td class="border px-1 py-0.5 text-[10px]">{{ question.expected }}</td>
              </tr>
            }
          }
        </tbody>
      </table>
    </section>

    <footer class="text-gray-600 text-sm mt-2">
      📧 info@auditfixers.com &nbsp;&nbsp; 🌐 www.auditfixers.com
    </footer>
  </section>

  <!-- Page 4 -->
  <section class="page font-sans text-gray-900 leading-relaxed py-5 px-15 max-w-3xl mx-auto flex flex-col justify-start print:h-screen print:page-break-after-always">

    <header class="mb-3 text-sm">
      ISO/IEC {{ standard === '42001' ? '42001:2023' : '27001:2022' }} Readiness Self-Assessment Report
    </header>

    <section class="mb-8 flex-1">
      <table class="w-full border border-black border-collapse mt-2 text-left no-repeat-header">
        <thead>
          <tr class="bg-gray-200">
            <th class="border px-1 py-0.5 text-[10px]">Question #</th>
            <th class="border px-1 py-0.5 text-[10px]">Requirement</th>
            <th class="border px-1 py-0.5 text-[10px]">Clause Ref</th>
            <th class="border px-1 py-0.5 text-[10px]">Your Response</th>
            <th class="border px-1 py-0.5 text-[10px]">What is Expected</th>
          </tr>
        </thead>

        <tbody>
          @for (section of pagedSections['page2']; track section.title) {
            <tr class="bg-gray-100 font-bold text-[10px]">
              <td colspan="5" class="border p-1 py-0.5 text-[10px]">
                Clause {{ section.questions.length > 0 ? getClauseNumber(section.questions[0]?.clauseRef) : 'N/A' }} – {{ section.title }}
              </td>
            </tr>

            @for (question of section.questions; track question.id) {
              <tr class="text-[10px]">
                <td class="border px-1 py-0.5 text-[10px]">{{ question.id }}</td>
                <td class="border px-1 py-0.5 text-[10px]">{{ question.label }}</td>
                <td class="border px-1 py-0.5 text-[10px]">{{ question.clauseRef }}</td>
                <td class="border px-1 py-0.5 text-[10px]"
                    [ngClass]="{
                      'bg-green-100 text-green-800 font-medium': question.value === 'Yes',
                      'bg-yellow-100 text-yellow-800 font-medium': question.value === 'Partial',
                      'bg-red-100 text-red-800 font-medium': question.value === 'No',
                      'bg-gray-100 text-gray-800 font-medium': question.value === 'N/A'
                    }">{{ question.value || '-' }}</td>
                <td class="border px-1 py-0.5 text-[10px]">{{ question.expected }}</td>
              </tr>
            }
          }
        </tbody>
      </table>
    </section>

    <footer class="text-gray-600 text-sm mt-2">
      📧 info@auditfixers.com &nbsp;&nbsp; 🌐 www.auditfixers.com
    </footer>
  </section>

  <!-- Page 5 -->
  <section class="page font-sans text-gray-900 leading-relaxed py-5 px-15 max-w-3xl mx-auto flex flex-col justify-start print:h-screen print:page-break-after-always">

    <header class="mb-3 text-sm">
      ISO/IEC {{ standard === '42001' ? '42001:2023' : '27001:2022' }} Readiness Self-Assessment Report
    </header>

    <section class="mb-8">
      <h3 class="text-md font-bold text-[#366091] pb-1">
        5. Recommendations and Next Steps
      </h3>

      <p>
        Based on the results of this self-assessment, we recommend reviewing all areas marked as 'Partial' or 'No' to identify critical compliance gaps. Your next steps should include developing a prioritized remediation plan, allocating necessary resources to address the identified vulnerabilities, and formally documenting the implemented controls. Once remediation is complete, consider scheduling a formal internal or external readiness audit.
      </p>
    </section>

    <footer class="text-gray-600 text-sm mt-2">
      📧 info@auditfixers.com &nbsp;&nbsp; 🌐 www.auditfixers.com
    </footer>
  </section>
</div>